import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../hooks/apiRequest";
import { SprintProps, SprintStatus, TaskProps } from "../types/interfaces";
import { sprintKeys, taskKeys } from "../utils/queryKeys";

// Sprints are global: one sprint can hold tasks from any project.
export function useSprints(status?: SprintStatus) {
  return useQuery({
    queryKey: sprintKeys.list(status),
    queryFn: async () => (await apiRequest<SprintProps[]>(`sprints${status ? `?status=${status}` : ""}`)).data,
  });
}

export function useSprintTasks(id: string | undefined) {
  return useQuery({
    queryKey: sprintKeys.tasks(id || ""),
    queryFn: async () => (await apiRequest<TaskProps[]>(`sprints/${id}/tasks`)).data,
    enabled: !!id,
  });
}

export function useCreateSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; goal?: string; startDate: string; endDate: string }) =>
      (await apiRequest<SprintProps>("sprints", { method: "POST", body: JSON.stringify(data) })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sprintKeys.all }),
  });
}

export function useUpdateSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<SprintProps> }) =>
      (await apiRequest<SprintProps>(`sprints/${id}`, { method: "PUT", body: JSON.stringify(data) })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sprintKeys.all }),
  });
}

export function useDeleteSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiRequest<void>(`sprints/${id}`, { method: "DELETE" })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sprintKeys.all });
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}
